import * as React from 'react'

import { XIcon } from 'lucide-react'
import { classList } from 'react-classlist-helper'

import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import Localization from '~/client/src/shared/localization/LocalizationManager'

interface ISectionProps {
  children?: React.ReactNode
  className?: string
}

interface IHeaderProps {
  title?: string
  onClose?: () => void
  /** Trailing controls before the close, e.g. a ⋯ menu; size them `icon-sm`. */
  actions?: React.ReactNode
  closeLabel?: string
  closeDisabled?: boolean
  closeTestId?: string
  className?: string
}

const Header: React.FC<IHeaderProps> = ({
  title,
  onClose,
  actions,
  closeLabel = Localization.translator.close,
  closeDisabled,
  closeTestId,
  className,
}) => {
  return (
    <div
      data-slot="panel-header"
      className={classList({
        'tw:flex tw:items-center tw:gap-2 tw:p-4 tw:bg-background tw:shrink-0': true,
        [className || '']: !!className,
      })}
    >
      <span
        className="tw:flex-1 tw:min-w-0 tw:truncate tw:text-body-lg tw:font-medium tw:text-foreground"
        title={title}
      >
        {title}
      </span>
      {/* 28px buttons on the 24px title row, 10px from the panel edge. */}
      <div className="tw:flex tw:items-center tw:gap-1 tw:shrink-0 tw:-my-0.5 tw:-mr-1.5">
        {actions}
        {onClose && (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={closeLabel}
            disabled={closeDisabled}
            data-testid={closeTestId}
            onClick={onClose}
          >
            <XIcon />
          </Button>
        )}
      </div>
    </div>
  )
}

const Content: React.FC<ISectionProps> = ({ children, className }) => {
  return (
    <div
      className={classList({
        'tw:flex-1 tw:min-h-0 tw:overflow-y-auto': true,
        [className || '']: !!className,
      })}
    >
      {children}
    </div>
  )
}

const Footer: React.FC<ISectionProps> = ({ children, className }) => {
  return (
    <div
      className={classList({
        'tw:flex tw:flex-col tw:shrink-0 tw:p-6 tw:border-0 tw:border-t tw:border-solid tw:border-border tw:bg-background': true,
        [className || '']: !!className,
      })}
    >
      {children}
    </div>
  )
}

export const PanelSections = {
  Header,
  Content,
  Footer,
}
